import { useCallback, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import {
  isCargoRatioImprovementRow,
  putCargoRatioCounterPricing,
  putCargoRatioPricing,
  resolveCargoRatioExistingFromRow,
} from '../../../shared/offer-domain/cargoRatio';
import { buildImprovementDyedUndyedPayload } from '../utils/improvementClarificationHelpers';

const toPayloadValue = value => {
  if (value === '' || value === undefined) return null;
  return value;
};

const useSetDyedUndyedImprovement = () => {
  const [isSaving, setIsSaving] = useState(false);

  const saveAlphaImprovement = useCallback(async payloadInput => {
    const token = getAuthToken();
    if (!token) {
      toast.error('Authentication required. Please login again.');
      return false;
    }

    const { detailRow, kind = 'offer', alpha = null } = payloadInput || {};

    setIsSaving(true);
    try {
      if (isCargoRatioImprovementRow(detailRow, kind)) {
        if (kind === 'counter') {
          const recordId =
            detailRow?.counterCargoRatioRecordId ??
            detailRow?.quantityInformationCargoRatioCounterId;
          if (!recordId) {
            toast.error('Cargo ratio counter ID is required.');
            return false;
          }

          await putCargoRatioCounterPricing({
            quantityInformationCargoRatioCounterId: recordId,
            existing: resolveCargoRatioExistingFromRow(detailRow),
            patch: {
              quantityInformationCargoRatioFK:
                detailRow?.quantityInformationCargoRatioId ??
                detailRow?.quantityInformationCargoRatioFK,
              quantityInformationCounterFK:
                detailRow?.quantityInformationCounterId,
              quantityIncotermCounterFK:
                detailRow?.quantityIncotermCounterId ?? null,
              cargoRatio:
                detailRow?.counterCargoRatioId ?? detailRow?.cargoRatio,
              parcelSize:
                detailRow?.cargoRatioParcelSize ??
                detailRow?.parcelSize ??
                null,
              alphaImprovement: toPayloadValue(alpha),
            },
            token,
          });
          return true;
        }

        const cargoRatioId = detailRow?.quantityInformationCargoRatioId;
        if (!cargoRatioId) {
          toast.error('Cargo ratio ID is required.');
          return false;
        }

        await putCargoRatioPricing({
          quantityInformationCargoRatioId: cargoRatioId,
          existing: resolveCargoRatioExistingFromRow(detailRow),
          patch: {
            quantityInformationFK: detailRow?.quantityInformationId,
            quantityIncotermFK: detailRow?.quantityIncotermId ?? null,
            cargoRatio: detailRow?.cargoRatio,
            parcelSize:
              detailRow?.cargoRatioParcelSize ?? detailRow?.parcelSize ?? null,
            alphaImprovement: toPayloadValue(alpha),
          },
          token,
        });
        return true;
      }

      const payload = buildImprovementDyedUndyedPayload(payloadInput);
      const isFobPayload = Array.isArray(payload?.listDyedUndyedAlphaFob);
      const detailId = isFobPayload
        ? payload.listDyedUndyedAlphaFob?.[0]?.quantityInformationDetailId
        : payload.listDyedUndyedAlpha?.[0]?.quantityInformationDetailId;

      if (!detailId) {
        toast.error('Quantity information detail ID is required.');
        return false;
      }

      if (
        isFobPayload &&
        !payload.listDyedUndyedAlphaFob?.[0]?.incotermLocationId
      ) {
        toast.error('Incoterm location ID is required for FOB Origin.');
        return false;
      }

      const endpoint = isFobPayload
        ? `${api.API_URL}/dmut/set-dyed-undyed-fob`
        : `${api.API_URL}/dmut/set-dyed-undyed`;
      await axios.post(endpoint, payload, {
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json-patch+json',
          Authorization: `Bearer ${token}`,
        },
      });
      return true;
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to save Alpha Improvement.';
      toast.error(message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, []);

  return {
    isSaving,
    saveAlphaImprovement,
  };
};

export default useSetDyedUndyedImprovement;
